import type { Command, JsonValue, RecordDefinition } from "@e308/core";
import { rebalanceShares } from "./army-shares.js";
import { validateArmyDestinations } from "./border-guards.js";
import type { SeatModel } from "./content-types.js";
import type { Micro } from "./exact-numbers.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { addTroops } from "./logistics-math.js";
import { homeTroops, setHomeTroops } from "./logistics-simulation.js";
import {
  type LogisticsState,
  type ReinforcementState,
  type TroopComposition,
  UNIT_IDS,
} from "./logistics-types.js";
import { routeProduction } from "./reinforcement-runtime.js";
import { initialReinforcement, validateShares } from "./reinforcement-state.js";
import type { ConflictIntent } from "./types.js";
import { setWaveTimer } from "./wave-timing.js";

export function allocateArmy(
  state: ReinforcementState,
  home: TroopComposition,
  shares: Record<string, number>,
  now: number,
) {
  const total = Object.values(state.gathered).reduce(addTroops, home);
  let next: ReinforcementState = {
    ...state,
    gathered: {},
    shares: Object.fromEntries(
      UNIT_IDS.map((unit) => [unit, shares]),
    ) as ReinforcementState["shares"],
  };
  for (const [id, share] of Object.entries(shares)) {
    if (!share) continue;
    next = { ...next, modes: { ...next.modes, [id]: "continuous" } };
    if (!next.timers?.[id] || state.modes[id] !== "continuous")
      next = setWaveTimer(next, id, next.timers?.[id]?.seconds ?? 5, now);
  }
  return routeProduction(next, total);
}

export function armyAllocationCommand(
  model: SeatModel,
  input: Extract<ConflictIntent, { kind: "allocate-army" | "adjust-army" }>,
  seats: ReadonlyMap<string, SeatModel>,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:allocate-army`,
    execute(tx) {
      const state = tx.getRecord(model.logistics) as LogisticsState;
      const intent = resolveArmyInput(input, state.reinforcement ?? initialReinforcement());
      const valid = validateShares(intent.shares, Object.keys(state.borders));
      validateArmyDestinations(
        tx,
        model,
        [...Object.keys(valid), ...(intent.wave ? [intent.wave.borderId] : [])],
        seats,
        lifecycle,
      );
      let routing = state.reinforcement ?? initialReinforcement();
      if (intent.wave)
        routing = setWaveTimer(routing, intent.wave.borderId, intent.wave.seconds, tx.gameTimeMs());
      const result = allocateArmy(routing, homeTroops(tx, model.resources), valid, tx.gameTimeMs());
      setHomeTroops(tx, model.resources, result.home);
      const updated: LogisticsState = {
        ...state,
        autoReinforce: [],
        reinforcement: result.state,
      };
      tx.setRecord(model.logistics, updated as LogisticsState & JsonValue);
    },
  };
}

function resolveArmyInput(
  input: Extract<ConflictIntent, { kind: "allocate-army" | "adjust-army" }>,
  routing: ReinforcementState,
): Extract<ConflictIntent, { kind: "allocate-army" }> {
  if (input.kind === "allocate-army") return input;
  if (typeof input.borderId !== "string" || !input.borderId || input.borderId.length > 128)
    throw new TypeError("Invalid border ID");
  const shares = routing.shares["guard-robot"];
  if (input.field === "share") {
    if (!Number.isInteger(input.value) || input.value < 0 || input.value > 100)
      throw new TypeError("Army share must be 0–100%");
    return { kind: "allocate-army", shares: rebalanceShares(shares, input.borderId, input.value) };
  }
  if (input.field !== "seconds") throw new TypeError("Unknown army control");
  return {
    kind: "allocate-army",
    shares: { ...shares },
    wave: { borderId: input.borderId, seconds: input.value },
  };
}
